<template>
  <b-tabs v-model="activeTab" class="user-list-tab" :vertical="true">
    <b-tab-item>
      <template #header>
        <!-- <b-icon icon="account-check" /> -->
        <span>
          <b-tag type="is-primary" rounded> {{ users.length }} </b-tag>
          {{ $t('users.approved') }}</span
        >
      </template>
    </b-tab-item>
    <b-tab-item>
      <template #header>
        <!-- <b-icon icon="account-clock" /> -->
        <span>
          <b-tag type="is-primary" rounded>
            {{ usersPendingApproval.length }}
          </b-tag>
          {{ $t('users.pending_approval') }}</span
        >
      </template>
    </b-tab-item>
    <b-tab-item>
      <template #header>
        <!-- <b-icon icon="login" /> -->
        <span
          ><b-tag type="is-primary" rounded>
            {{ usersLoggedInToday.length }}
          </b-tag>
          {{ $t('users.todays_logins') }}</span
        >
      </template>
    </b-tab-item>
    <b-tab-item v-if="isCurrentUserPowerAdmin">
      <template #header>
        <!-- <b-icon icon="account-cancel" /> -->
        <span
          ><b-tag type="is-primary" rounded>
            {{ usersRejected.length }}
          </b-tag>
          {{ $t('users.rejected') }}
        </span>
      </template>
    </b-tab-item>
    <b-tab-item>
      <template #header>
        <span
          ><b-tag type="is-primary" rounded>
            {{ usersWithoutSepiaInfo.length }}
          </b-tag>
          {{ $t('users.inactive') }}
        </span>
      </template>
    </b-tab-item>

    <div class="user-list">
      <template v-if="activeTab === 0">
        <p>{{ $t('users.approved_description') }}</p>
        <user-list :users="users" @selected="editUserProfile($event)" />
      </template>
      <template v-else-if="activeTab === 1">
        <p>{{ $t('users.pending_approval_description') }}</p>
        <user-list
          :users="usersPendingApproval"
          @selected="editUserProfile($event)"
        />
      </template>
      <template v-else-if="activeTab === 2">
        <p>{{ $t('users.todays_logins_description') }}</p>
        <user-list
          :users="usersLoggedInToday"
          @selected="editUserProfile($event)"
        />
      </template>
      <template v-else-if="activeTab === rejectedTabIndex">
        <p>{{ $t('users.rejected_description') }}</p>
        <user-list :users="usersRejected" @selected="editUserProfile($event)" />
      </template>
      <template v-else-if="activeTab === inactiveTabIndex">
        <p>{{ $t('users.inactive_description') }}</p>
        <user-list
          :users="usersWithoutSepiaInfo"
          @selected="editUserProfile($event)"
        />
      </template>
    </div>
  </b-tabs>
</template>

<script>
import { debounce } from 'lodash';
import {
  USERS_GET_USERS,
  USERS_GET_USERS_PENDING_APPROVAL,
  USERS_GET_USERS_LOGGED_IN_TODAY,
  USERS_GET_USERS_REJECTED,
  USERS_GET_USERS_WITHOUT_SEPIA_INFO,
} from '../../store/users/actionTypes';
import {
  SET_USERS,
  SET_USERS_PENDING_APPROVAL,
  SET_USERS_LOGGED_IN_TODAY,
  SET_USERS_REJECTED,
  SET_USERS_WITHOUT_SEPIA_INFO,
} from '../../store/users/mutations/mutationTypes';
import {
  GET_USERS,
  GET_USERS_PENDING_APPROVAL,
  GET_USERS_LOGGED_IN_TODAY,
  GET_USERS_REJECTED,
  GET_USERS_WITHOUT_SEPIA_INFO,
} from '../../store/users/getters/getterTypes';
import UserList from './UserList';

const ADMIN_USERS_ACTIVE_TAB_STORAGE_KEY = 'admin_users_active_tab';

export default {
  name: 'users',
  props: {
    search: String,
  },
  components: {
    'user-list': UserList,
  },
  data() {
    return {
      activeTab: 0,
      isLoading: false,
      isSearchActive: false,
      loadedTabs: new Set(),
      searchCache: new Map(),
      baseUsersCache: null,
      pendingSearchQuery: null,
      searchDisplayCache: null,
    };
  },

  watch: {
    search: {
      handler(newSearch) {
        if (newSearch) {
          this.debouncedSearch(newSearch);
        } else {
          this.clearSearch();
        }
      },
    },
    activeTab(newTab) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(
          ADMIN_USERS_ACTIVE_TAB_STORAGE_KEY,
          String(newTab)
        );
      }
    },
  },

  created() {
    this.debouncedSearch = debounce((query) => {
      this.handleSearch(query);
    }, 500);

    const persistedActiveTab =
      typeof window !== 'undefined'
        ? window.sessionStorage.getItem(ADMIN_USERS_ACTIVE_TAB_STORAGE_KEY)
        : null;
    const parsedActiveTab = Number.parseInt(persistedActiveTab, 10);

    if (!Number.isNaN(parsedActiveTab)) {
      this.activeTab = this.normalizeActiveTab(parsedActiveTab);
    }
  },

  async mounted() {
    if (this.isAdminUsersLoaded && this.sites.length > 0) {
      this.hydrateUsersStateFromStore();
    } else {
      await this.loadAllUserData();
    }

    this.activeTab = this.normalizeActiveTab(this.activeTab);

    const persistedSearch = (this.search || '').trim();
    if (persistedSearch) {
      await this.handleSearch(persistedSearch);
    }
  },

  computed: {
    users() {
      return this.formatUserData(this.getDisplaySource('users', GET_USERS, []));
    },
    usersPendingApproval() {
      return this.formatUserData(
        this.getDisplaySource(
          'usersPendingApproval',
          GET_USERS_PENDING_APPROVAL,
          []
        )
      );
    },
    usersLoggedInToday() {
      return this.formatUserData(
        this.getDisplaySource(
          'usersLoggedInToday',
          GET_USERS_LOGGED_IN_TODAY,
          []
        )
      );
    },
    usersRejected() {
      const fallback = this.isCurrentUserPowerAdmin
        ? this.$store.getters[GET_USERS_REJECTED]
        : [];
      return this.formatUserData(
        this.getDisplaySource('usersRejected', GET_USERS_REJECTED, fallback)
      );
    },
    usersWithoutSepiaInfo() {
      return this.formatUserData(
        this.getDisplaySource(
          'usersWithoutSepiaInfo',
          GET_USERS_WITHOUT_SEPIA_INFO,
          []
        )
      );
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    sites() {
      return this.$store.state.sites;
    },
    isAdminUsersLoaded() {
      return Boolean(this.$store.state.UtilitiesModule.admin_users_loaded);
    },
    isCurrentUserPowerAdmin() {
      return (
        this.currentUser.role === 'admin' &&
        parseInt(this.currentUser.privil, 10) === 1
      );
    },
    rejectedTabIndex() {
      return this.isCurrentUserPowerAdmin ? 3 : -1;
    },
    inactiveTabIndex() {
      return this.isCurrentUserPowerAdmin ? 4 : 3;
    },
  },

  methods: {
    normalizeActiveTab(activeTab) {
      const maxTabIndex = this.isCurrentUserPowerAdmin ? 4 : 3;
      if (!Number.isInteger(activeTab)) return 0;
      if (activeTab < 0) return 0;
      if (activeTab > maxTabIndex) return maxTabIndex;
      return activeTab;
    },

    async loadAllUserData() {
      this.isLoading = true;
      try {
        const requests = [
          this.$store.dispatch(USERS_GET_USERS),
          this.$store.dispatch(USERS_GET_USERS_PENDING_APPROVAL),
          this.$store.dispatch(USERS_GET_USERS_LOGGED_IN_TODAY),
          this.$store.dispatch(USERS_GET_USERS_WITHOUT_SEPIA_INFO),
          this.$store.dispatch('getSites'),
        ];

        if (this.isCurrentUserPowerAdmin) {
          requests.push(this.$store.dispatch(USERS_GET_USERS_REJECTED));
        }

        await Promise.all(requests);

        // Mark all tabs as loaded
        [0, 1, 2, 3, 4].forEach((tab) => this.loadedTabs.add(tab));

        this.baseUsersCache = this.buildUsersCacheFromStore();
        this.$store.commit('UtilitiesModule/SET_ADMIN_USERS_LOADED', {
          admin_users_loaded: true,
        });

        if (this.pendingSearchQuery) {
          const pendingQuery = this.pendingSearchQuery;
          this.pendingSearchQuery = null;
          await this.handleSearch(pendingQuery);
        }
      } catch (err) {
        console.error('Error loading users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    hydrateUsersStateFromStore() {
      this.loadedTabs.clear();
      [0, 1, 2, 3, 4].forEach((tab) => this.loadedTabs.add(tab));
      this.baseUsersCache = this.buildUsersCacheFromStore();
      this.searchDisplayCache = null;
      this.pendingSearchQuery = null;
    },

    async loadApprovedUsers(query = {}) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(USERS_GET_USERS, {
          ...query,
          ...(this.search && { search: this.search }),
        });
      } catch (err) {
        console.error('Error loading approved users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async loadPendingApprovalUsers(query = {}) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(USERS_GET_USERS_PENDING_APPROVAL, {
          ...query,
          ...(this.search && { search: this.search }),
        });
      } catch (err) {
        console.error('Error loading pending approval users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async loadLoggedInTodayUsers(query = {}) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(USERS_GET_USERS_LOGGED_IN_TODAY, {
          ...query,
          ...(this.search && { search: this.search }),
        });
      } catch (err) {
        console.error('Error loading logged in today users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async loadRejectedUsers(query = {}) {
      if (!this.isCurrentUserPowerAdmin) return;

      this.isLoading = true;
      try {
        await this.$store.dispatch(USERS_GET_USERS_REJECTED, {
          ...query,
          ...(this.search && { search: this.search }),
        });
      } catch (err) {
        console.error('Error loading rejected users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async loadInactiveUsers(query = {}) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(USERS_GET_USERS_WITHOUT_SEPIA_INFO, {
          ...query,
          ...(this.search && { search: this.search }),
        });
      } catch (err) {
        console.error('Error loading inactive users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async handleSearch(query) {
      this.ensureBaseUsersCache();

      if (this.baseUsersCache) {
        this.isSearchActive = true;
        const filtered = this.filterUsersFromCache(query, this.baseUsersCache);
        this.applyUsersCache(filtered);
        return;
      }

      if (this.isLoading) {
        this.pendingSearchQuery = query;
        return;
      }

      // Check cache first
      if (this.searchCache.has(query)) {
        console.log('Using cached search results for:', query);
        const cachedData = this.searchCache.get(query);

        this.isSearchActive = true;
        this.applyUsersCache(cachedData);
        return;
      }

      this.loadedTabs.clear();
      this.isSearchActive = true;

      this.isLoading = true;
      try {
        const requests = [
          this.$store.dispatch(USERS_GET_USERS, {
            search: query,
          }),
          this.$store.dispatch(USERS_GET_USERS_PENDING_APPROVAL, {
            search: query,
          }),
          this.$store.dispatch(USERS_GET_USERS_LOGGED_IN_TODAY, {
            search: query,
          }),
          this.$store.dispatch(USERS_GET_USERS_WITHOUT_SEPIA_INFO, {
            search: query,
          }),
        ];

        if (this.isCurrentUserPowerAdmin) {
          requests.push(
            this.$store.dispatch(USERS_GET_USERS_REJECTED, {
              search: query,
            })
          );
        }

        await Promise.all(requests);

        // Cache the results (limit cache size to 20 entries)
        if (this.searchCache.size >= 20) {
          const firstKey = this.searchCache.keys().next().value;
          this.searchCache.delete(firstKey);
        }

        this.searchCache.set(query, {
          users: [...this.$store.getters[GET_USERS]],
          usersPendingApproval: [
            ...this.$store.getters[GET_USERS_PENDING_APPROVAL],
          ],
          usersLoggedInToday: [
            ...this.$store.getters[GET_USERS_LOGGED_IN_TODAY],
          ],
          usersWithoutSepiaInfo: [
            ...this.$store.getters[GET_USERS_WITHOUT_SEPIA_INFO],
          ],
          usersRejected: this.isCurrentUserPowerAdmin
            ? [...this.$store.getters[GET_USERS_REJECTED]]
            : null,
        });

        // Mark all tabs as loaded with search
        [0, 1, 2, 3, 4].forEach((tab) =>
          this.loadedTabs.add(`${tab}-search-${query}`)
        );
      } catch (err) {
        console.error('Error searching users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async clearSearch() {
      this.isSearchActive = false;
      if (this.debouncedSearch && this.debouncedSearch.cancel) {
        this.debouncedSearch.cancel();
      }
      this.pendingSearchQuery = null;
      this.searchDisplayCache = null;
      this.ensureBaseUsersCache();
      if (this.baseUsersCache) {
        this.applyUsersCache(this.baseUsersCache, { forceStore: true });
        return;
      }
      await this.loadAllUserData();
    },

    beforeDestroy() {
      if (this.debouncedSearch && this.debouncedSearch.cancel) {
        this.debouncedSearch.cancel();
      }
    },

    editUserProfile(user) {
      const rawUser = this.findRawUserByEmail(user?._id) || user;
      this.$router.push({
        name: 'edit-user',
        params: { user: rawUser, id: rawUser._id },
      });
    },

    findRawUserByEmail(email) {
      if (!email) return null;

      const baseCacheLists = this.baseUsersCache
        ? [
            this.baseUsersCache.users,
            this.baseUsersCache.usersPendingApproval,
            this.baseUsersCache.usersLoggedInToday,
            this.baseUsersCache.usersWithoutSepiaInfo,
            this.baseUsersCache.usersRejected,
          ]
        : [];

      const storeLists = [
        this.$store.getters[GET_USERS],
        this.$store.getters[GET_USERS_PENDING_APPROVAL],
        this.$store.getters[GET_USERS_LOGGED_IN_TODAY],
        this.$store.getters[GET_USERS_WITHOUT_SEPIA_INFO],
        this.isCurrentUserPowerAdmin
          ? this.$store.getters[GET_USERS_REJECTED]
          : [],
      ];

      return [...baseCacheLists, ...storeLists]
        .filter(Boolean)
        .flat()
        .find((currentUser) => currentUser._id === email);
    },

    applyUsersCache(cache, { forceStore = false } = {}) {
      if (this.isSearchActive && !forceStore) {
        this.searchDisplayCache = cache;
        return;
      }

      this.commitUsersCacheToStore(cache);
      this.searchDisplayCache = null;
    },

    ensureBaseUsersCache() {
      if (this.baseUsersCache) return;

      const hasAllTabsLoaded = this.isCurrentUserPowerAdmin
        ? [0, 1, 2, 3, 4].every((tab) => this.loadedTabs.has(tab))
        : [0, 1, 2, 4].every((tab) => this.loadedTabs.has(tab));

      if (!hasAllTabsLoaded) return;

      this.baseUsersCache = this.buildUsersCacheFromStore();
    },

    getDisplaySource(cacheKey, getterKey, fallback) {
      if (this.isSearchActive && this.searchDisplayCache) {
        return this.searchDisplayCache[cacheKey] || fallback;
      }
      return this.$store.getters[getterKey] || fallback;
    },

    buildUsersCacheFromStore() {
      return {
        users: [...this.$store.getters[GET_USERS]],
        usersPendingApproval: [
          ...this.$store.getters[GET_USERS_PENDING_APPROVAL],
        ],
        usersLoggedInToday: [...this.$store.getters[GET_USERS_LOGGED_IN_TODAY]],
        usersWithoutSepiaInfo: [
          ...this.$store.getters[GET_USERS_WITHOUT_SEPIA_INFO],
        ],
        usersRejected: this.isCurrentUserPowerAdmin
          ? [...this.$store.getters[GET_USERS_REJECTED]]
          : null,
      };
    },

    commitUsersCacheToStore(cache) {
      this.$store.commit(SET_USERS, cache.users);
      this.$store.commit(
        SET_USERS_PENDING_APPROVAL,
        cache.usersPendingApproval
      );
      this.$store.commit(SET_USERS_LOGGED_IN_TODAY, cache.usersLoggedInToday);
      this.$store.commit(
        SET_USERS_WITHOUT_SEPIA_INFO,
        cache.usersWithoutSepiaInfo
      );
      if (this.isCurrentUserPowerAdmin && cache.usersRejected) {
        this.$store.commit(SET_USERS_REJECTED, cache.usersRejected);
      }
    },

    filterUsersFromCache(query, cache) {
      const normalizedQuery = (query || '').trim().toLowerCase();
      if (!normalizedQuery) return cache;
      const matches = (user) => {
        const fullname = (user.fullname || '').toLowerCase();
        const email = (user._id || '').toLowerCase();
        const location = (user.location || '').toLowerCase();
        const siteFound = this.sites.find((site) => site._id === user.location);
        const locationDisplay = (siteFound?.siteName || '').toLowerCase();
        const role = (user.role || '').toLowerCase();
        const roleDisplay =
          parseInt(user.privil, 10) === 1 ? 'power admin' : role;
        return (
          fullname.includes(normalizedQuery) ||
          email.includes(normalizedQuery) ||
          location.includes(normalizedQuery) ||
          locationDisplay.includes(normalizedQuery) ||
          role.includes(normalizedQuery) ||
          roleDisplay.includes(normalizedQuery)
        );
      };

      return {
        users: cache.users.filter(matches),
        usersPendingApproval: cache.usersPendingApproval.filter(matches),
        usersLoggedInToday: cache.usersLoggedInToday.filter(matches),
        usersWithoutSepiaInfo: cache.usersWithoutSepiaInfo.filter(matches),
        usersRejected: this.isCurrentUserPowerAdmin
          ? (cache.usersRejected || []).filter(matches)
          : null,
      };
    },

    formatUserData(users) {
      return users.map((user) => {
        const siteFound = this.sites.find((site) => site._id === user.location);
        const formattedUser = { ...user };

        formattedUser.date = new Date(user.timestamp).toLocaleDateString();

        if (parseInt(user.privil, 10) === 1) {
          formattedUser.role = 'Power Admin';
        }

        formattedUser.location = siteFound ? siteFound.siteName : user.location;

        if (user.lastLoginDate) {
          formattedUser.lastLoginTimestamp = user.lastLoginDate;

          formattedUser.lastLoginFormatted = new Date(
            user.lastLoginDate
          ).toLocaleString(undefined, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        } else {
          formattedUser.lastLoginTimestamp = 0;
          formattedUser.lastLoginFormatted = 'N/A';
        }

        return formattedUser;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.content {
  display: flex;
}
.user-list-tab {
  margin-top: 20px;
  margin-left: 20px;
}
.user-list {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  margin-left: 20px;
  margin-right: 20px;

  p {
    background-color: #f5f5f5;
    margin-bottom: 20px;
  }
}
</style>
